import { Auth } from "@aws-amplify/auth"
import { useMountEffect } from "shared/src/libs/hooksLib/hooksLib"
import QRCode from "react-qr-code"
import { LoadingSpinner } from "../LoaderButton/LoaderButton"
import { useState } from "react"
import { SignUpForm } from "../SignUpComponents/SignUpForm/SignUpForm/SignUpForm"
import { SignUpCardWithImage } from "../SignUpComponents/SignUpCardWithImage"

export function MFASetup({
	signinResponse,
	onSubmit,
	fields,
	handleFieldChange,
	sessionOverrideRef,
}: Readonly<{
	signinResponse: any
	fields: { mfaCode: string }
	handleFieldChange: (event: { target: { id: string; value: any } }) => void
	onSubmit: (e: any) => Promise<void>
	next?: boolean
	sessionOverrideRef?: React.MutableRefObject<object>
}>) {
	const [strForCode, setStrForCode] = useState(() => "")
	const [isLoading, setIsLoading] = useState(false)

	useMountEffect(() => {
		Auth.setupTOTP(signinResponse).then((code) => {
			setStrForCode("otpauth://totp/AWSCognito:" + signinResponse.username + "?secret=" + code + "&issuer=ARAAdminSite")
		})
	})

	async function onSubmitInt(e) {
		try {
			setIsLoading(true)
			e.preventDefault()
			console.log("verifying token")
			const verifyResponse = await Auth.verifyTotpToken(signinResponse, fields.mfaCode)
			sessionOverrideRef.current = verifyResponse
			await Auth.setPreferredMFA(signinResponse, "TOTP")
			await onSubmit?.(e)
		} catch (e) {
			alert(e)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<MFAWrap
			size="large"
			fields={fields}
			handleFieldChange={handleFieldChange}
			onSubmit={onSubmitInt}
			isLoading={isLoading}
			isValid={fields.mfaCode.length === 6}>
			{strForCode != null ? (
				<div style={{ textAlign: "center" }}>
					<h2 className="bs-h2">Please scan the QR code from an authenticator application (eg Google Authenticator)</h2>
					<QRCode style={{ margin: "2rem 0 1rem 0" }} value={strForCode} />
					<hr className="mfa-form-divider" />
					{/* <Form.Text>{`(Alternatively you may set up the autenticator by entering the following code in the authenticator application: ${strForCode})`}</Form.Text> */}
				</div>
			) : (
				<LoadingSpinner isLoading={true} />
			)}
		</MFAWrap>
	)
}

export function MFAValidateInner({
	signinResponse,
	fields,
	handleFieldChange,
	onSubmit,
	next,
}: Readonly<{
	signinResponse: any
	fields: { mfaCode: string }
	handleFieldChange: (event: { target: { id: string; value: any } }) => void
	onSubmit: (e: any) => Promise<void>
	next?: boolean
}>) {
	const [isLoading, setIsLoading] = useState(false)

	useMountEffect(() => {
		if (fields.mfaCode?.length > 0) {
			handleFieldChange({ target: { id: "mfaCode", value: "" } })
		}
	})

	async function onSubmitInt(e) {
		try {
			setIsLoading(true)
			e.preventDefault()
			console.log("confirming signin", signinResponse)
			const confirmResponse = await Auth.confirmSignIn(signinResponse, fields.mfaCode, "SOFTWARE_TOKEN_MFA")
			console.log("signin confirmed", confirmResponse)
			await onSubmit(e)
		} catch (e) {
			alert(e)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<MFAWrapInner
			size="medium"
			fields={fields}
			handleFieldChange={handleFieldChange}
			onSubmit={onSubmitInt}
			isLoading={isLoading}
			isValid={fields.mfaCode.length === 6}
			next={next}></MFAWrapInner>
	)
}

export function MFAValidate(
	props: Readonly<{
		signinResponse: any
		fields: { mfaCode: string }
		handleFieldChange: (event: { target: { id: string; value: any } }) => void
		onSubmit: (e: any) => Promise<void>
		next?: boolean
	}>
) {
	return (
		<SignUpCardWithImage size="medium">
			<MFAValidateInner {...props} />
		</SignUpCardWithImage>
	)
}

function MFAWrap(
	props: Readonly<{
		fields: { mfaCode: string }
		handleFieldChange: (event: { target: { id: string; value: any } }) => void
		onSubmit: (e: any) => void
		children?: any
		isValid: boolean
		isLoading: boolean
		size: "medium" | "large"
		next?: boolean
	}>
) {
	return (
		<SignUpCardWithImage size={props.size}>
			<MFAWrapInner {...props}>{props.children}</MFAWrapInner>
		</SignUpCardWithImage>
	)
}

function MFAWrapInner({
	fields,
	handleFieldChange,
	onSubmit,
	children,
	isValid,
	isLoading,
	size,
	next,
}: Readonly<{
	fields: { mfaCode: string }
	handleFieldChange: (event: { target: { id: string; value: any } }) => void
	onSubmit: (e: any) => void
	children?: any
	isValid: boolean
	isLoading: boolean
	size: "medium" | "large"
	next?: boolean
}>) {
	return (
		<div className={`mfa-form-container mfa-form-container-${size}`}>
			{children}
			<SignUpForm
				onSubmit={onSubmit}
				title={
					<h2 className="bs-h2">
						Please enter the{next ? <span className="redText"> NEXT</span> : ""} 6 digit code shown in your MFA
						application
					</h2>
				}
				button={{
					isLoading: isLoading,
					text: "Continue",
					role: "submit",
					type: "submit",
					onClick: onSubmit,
					disabled: !isValid,
				}}
				fields={[
					{
						label: "MFA Code",
						type: "text",
						placeholder: "Enter MFA Code",
						fieldName: "mfaCode",
						initialValue: fields.mfaCode,
						autofocus: true,

						required: true,
						inputFieldHandler: handleFieldChange,
						description: (
							<span
								style={
									fields.mfaCode?.length === 6 || fields.mfaCode?.length === 0
										? { display: "none" }
										: { color: "var(--ara-error)" }
								}>
								Must be a 6 digit code
							</span>
						),
					},
				]}
			/>
		</div>
	)
}
